import React from 'react';
import { Box } from '@mui/material';

/**
 * StickyFormActions — the one place this app decides where a form's commit button goes.
 *
 * WHY LEFT, AND WHY PRIMARY FIRST
 * -------------------------------
 * Seven pages had grown their own copy of this bar, all of them `justifyContent: 'flex-end'` with
 * the order `[Cancel] [Save]`. The evidence points the other way for a FORM:
 *
 *   Luke Wroblewski's eye-tracking work, quoted in Adam Silver's "Where to put buttons on forms"
 *   (2019-09-16): align the primary button with the left edge of the inputs — "illuminate a clear
 *   path to completion. Aligning inputs and actions with a strong vertical axis clearly
 *   communicates how to go about completing a form."
 *
 *   Accessibility, same source: a left-aligned button "helps screen magnifier users to see it
 *   without having to pan across". A right-aligned button at 400% zoom is simply off-screen. On a
 *   settings page this is the difference between reachable and not.
 *
 *   Order: Wroblewski's research puts cancel to the RIGHT of the primary and styled less
 *   prominently. Caroline Jarrett's rule — make destructive actions harder to find — is why a
 *   Remove/Delete never belongs in this bar at all; it lives in the page header.
 *
 * WHERE THIS RULE STOPS
 * ---------------------
 * Dialogs are NOT forms in this sense. `DialogActions` stays right-aligned with the primary last,
 * because a dialog is a modal decision with no field column to align to, and right-last is the
 * platform convention on both Apple and Material. Do not "unify" MUI dialog footers with this bar.
 *
 * The `note` slot is pushed to the far right on purpose: a warning such as "pick a time zone first"
 * is information, not an action, and putting it between the field column and the buttons would break
 * the vertical axis the primary button is there to continue.
 */
export interface StickyFormActionsProps {
  /** The commit action. Rendered FIRST, on the form's left axis. */
  primary: React.ReactNode;
  /** Cancel / discard. Rendered after the primary and should be visually weaker. */
  secondary?: React.ReactNode;
  /** Another action directly related to the form, e.g. "Save & Test". Sits after the pair. */
  extra?: React.ReactNode;
  /** Status or warning text. Pushed to the far right; never between the axis and the buttons. */
  note?: React.ReactNode;
  /**
   * Horizontal spacing units to bleed outwards, cancelling the container's own padding so the bar's
   * top border spans its full width. `5.25` (42px) for a page-gutter bar, `2.5` for one scoped
   * inside a card.
   */
  bleed?: number;
  /** Cancels a card's bottom padding so the bar sits flush on its edge. Page bars leave this at 0. */
  flushBottom?: number;
}

const StickyFormActions: React.FC<StickyFormActionsProps> = ({
  primary,
  secondary,
  extra,
  note,
  bleed = 5.25,
  flushBottom = 0,
}) => (
  <Box
    sx={{
      position: 'sticky',
      bottom: flushBottom ? -flushBottom * 8 : 0,
      left: 0,
      right: 0,
      mt: 2,
      mx: -bleed,
      px: bleed,
      py: 1.5,
      bgcolor: 'background.paper',
      borderTop: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
      display: 'flex',
      alignItems: 'center',
      // Primary first, on the form's left axis. See the docblock.
      justifyContent: 'flex-start',
      gap: 1.5,
      zIndex: 10,
    }}
  >
    {primary}
    {secondary}
    {extra}
    {note ? <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>{note}</Box> : null}
  </Box>
);

export default StickyFormActions;
